import { useCallback, useEffect, useState } from "react";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useAdminApi, saveBlob } from "@/lib/admin";
import { NativeSelect } from "@/components/admin/kit";
import { StatusBadge } from "@/components/site/Notices";
import { summaryRows } from "@/components/apply/StepsB";

const SETTABLE = ["under_review", "shortlisted", "interview_scheduled", "selected", "rejected", "withdrawn"];

export const AppDetail = ({ id, onClose }) => {
  const call = useAdminApi();
  const [a, setA] = useState(null);
  const [form, setForm] = useState({ status: "", interview_status: "", interview_date: "", note: "" });
  const load = useCallback(() => call("get", `/admin/applications/${id}`).then((d) => { setA(d); setForm({ status: "", interview_status: d.interview_status || "", interview_date: d.interview_date || "", note: "" }); }), [call, id]);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      await call("patch", `/admin/applications/${id}`, { status: form.status || null, interview_status: form.interview_status, interview_date: form.interview_date, note: form.note || null });
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const download = async (d) => {
    try {
      saveBlob(await call("get", `/admin/applications/${id}/documents/${d.id}`, undefined, { responseType: "blob" }), d.filename);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const s = a && summaryRows(a);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="application-detail-dialog">
        <DialogHeader><DialogTitle className="font-display">{a ? `${a.reference} · ${a.personal.full_name}` : "Loading…"}</DialogTitle></DialogHeader>
        {a && (
          <div className="print-area space-y-5 text-sm">
            <div className="flex flex-wrap gap-2"><StatusBadge status={a.status} testid="detail-status" /><StatusBadge status={a.payment_status} testid="detail-payment-status" />{a.refund_status && <StatusBadge status={a.refund_status === "refunded" ? "refunded" : "refund_pending"} />}</div>
            {[["Position", s.job], ["Personal", s.personal], ["Education", s.education]].map(([t, rows]) => (
              <div key={t}><h4 className="mb-2 font-display font-bold">{t}</h4><dl className="grid gap-2 sm:grid-cols-2">{rows.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="break-words font-medium">{v || "—"}</dd></div>)}</dl></div>
            ))}
            <div><h4 className="mb-2 font-display font-bold">Documents</h4>{a.documents.map((d) => <Button key={d.id} variant="outline" size="sm" className="mb-2 mr-2" onClick={() => download(d)} data-testid={`detail-download-${d.doc_type}`}><Download className="h-3.5 w-3.5" /> {d.doc_type}: {d.filename}</Button>)}</div>
            <div><h4 className="mb-2 font-display font-bold">Payments</h4>{a.payments.map((p) => <p key={p.id} className="font-mono-num text-xs">{p.razorpay_order_id} · {p.razorpay_payment_id || "—"} · {inr(p.amount)} · {p.status} · {p.receipt_no || ""}</p>)}{!a.payments.length && <p className="text-slate-500">No payment attempts.</p>}</div>
            <div className="no-print space-y-3 rounded-xl bg-slate-50 p-4">
              <h4 className="font-display font-bold">Recruitment progress</h4>
              <div className="flex flex-wrap gap-2">
                <NativeSelect value={form.status} onChange={(v) => setForm({ ...form, status: v })} placeholder="Change status…" options={SETTABLE} testid="detail-status-select" />
                <Input className="h-9 w-48" placeholder="Interview status" value={form.interview_status} onChange={(e) => setForm({ ...form, interview_status: e.target.value })} data-testid="detail-interview-status" />
                <Input type="datetime-local" className="h-9 w-56" value={form.interview_date} onChange={(e) => setForm({ ...form, interview_date: e.target.value })} data-testid="detail-interview-date" />
              </div>
              <Textarea placeholder="Add internal note (not visible to applicant)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="detail-note-input" />
              <p className="text-xs text-slate-500">Paid / Submitted is set only by verified Razorpay payment and cannot be chosen manually.</p>
              <div className="flex gap-2"><Button onClick={save} data-testid="detail-save-button">Save</Button><Button variant="outline" onClick={() => window.print()} data-testid="detail-print-button"><Printer className="h-4 w-4" /> Print</Button></div>
            </div>
            <div><h4 className="mb-2 font-display font-bold">Notes & history</h4>
              {(a.internal_notes || []).map((n) => <p key={n.id} className="mb-1 text-xs"><strong>{n.by}</strong> · {fmtDate(n.at, true)}: {n.text}</p>)}
              {(a.history || []).map((h, i) => <p key={i} className="text-xs text-slate-500">{fmtDate(h.at, true)} → {h.status} ({h.by})</p>)}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
